// RentalCard.tsx
"use client";

import FavoriteIconButton from "./card-component/FavoriteIconButton";
import RatingReviewBadge from "./card-component/RatingReviewBadge";
import CardStatusBadges from "./card-component/CardStatusBadges";
import Image from "next/image";
import PriceDisplay from "./card-component/PriceDisplay";
import Link from "next/link";
import { memo, useMemo } from "react";
import { ServiceIcon } from "../services/ServiceMeta";
import { FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Typography } from "../Typography";
import {
  IconBike,
  IconBuildingStore,
  IconMapPin,
  IconMotorbike,
} from "@tabler/icons-react";
import { RentalCarouselItem } from "../main-services/rentals/rental.type";
import { ServiceCardIconKey } from "../services/service.card.types";
import { useRouter } from "next/navigation";
import { useRentalHref } from "@/hooks/url-state/useRentalQuery";

const ATTRIBUTE_ICON_MAP: Record<string, ServiceCardIconKey> = {
  IconGasStation: "fuel",
  IconHelmet: "helmet",
  IconLock: "lock",
  IconBottle: "bottle",
};

interface RentalCornerDiscountBadgeProps {
  label: string;
  className?: string;
}

export const RentalCornerDiscountBadge = ({
  label,
  className,
}: RentalCornerDiscountBadgeProps) => {
  return (
    <div
      className={cn(
        "pointer-events-none absolute top-0 right-0 z-20 size-[50px] overflow-hidden rounded-tr-2xl",
        className,
      )}
      aria-label={`${label} discount`}
    >
      <svg
        viewBox="0 0 52 52"
        className="size-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polygon points="0,0 52,0 52,52" fill="#FF001F" />
        <text
          x="33"
          y="18"
          fill="#FFFFFF"
          fontSize="11"
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="central"
          transform="rotate(45 33 18)"
          style={{ fontFamily: "inherit" }}
        >
          {label}
        </text>
      </svg>
    </div>
  );
};

interface RentalCardProps {
  rental: RentalCarouselItem;
  className?: string;
  href?: string;
  layout?: "row" | "stacked";
}

const RentalCard = ({
  rental,
  className,
  layout = "row",
  href,
}: RentalCardProps) => {
  const router = useRouter();
  const finalHref = useRentalHref(rental, href);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("a")) {
      return;
    }
    router.push(finalHref as never);
  };

  const price = Number(rental.price);
  const salePrice = Number(rental.salePrice);
  const hasSalePrice =
    Number.isFinite(salePrice) && salePrice > 0 && salePrice !== price;

  const discountLabel = useMemo(() => {
    const dis = Number(rental.disPercent);
    if (Number.isFinite(dis) && dis > 0) {
      return `-${dis}%`;
    }
    if (hasSalePrice && price > salePrice) {
      const diff = Math.round((price - salePrice) * 100) / 100;
      return `${diff}$ Off`;
    }
    return null;
  }, [rental.disPercent, hasSalePrice, price, salePrice]);

  const quickIcons = (rental.attributes ?? [])
    .map((attr) => ATTRIBUTE_ICON_MAP[attr.iconName])
    .filter((icon): icon is ServiceCardIconKey => Boolean(icon));

  const isBicycle = rental.vehicleType === "bike";
  const primaryMetaText = isBicycle
    ? (rental.vehicleCategory?.name ?? "Bicycle")
    : rental.transmissSystem === "automatic"
      ? "Automatic"
      : "Manual";

  const primaryMetaTrailing = isBicycle
    ? undefined
    : (rental.engineCapacity ?? undefined);
  const isStacked = layout === "stacked";
  const cardStyle =
    layout === "stacked"
      ? "flex flex-row truncate px-2 gap-1 items-center"
      : "flex flex-row gap-1 truncate items-center";

  const imageBlock = (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden",
        isStacked
          ? "w-full h-[180px] rounded-xl"
          : "w-[190px] h-[145px] rounded-br-xl",
      )}
    >
      <Image
        src={rental.feature ?? FALLBACK_FEATURE_IMAGE}
        alt={"rental-feature-image-card"}
        fill
        className={cn(
          "object-cover transform-gpu transition-transform duration-300 ease-out will-change-transform group-hover:scale-110",
          isStacked ? "rounded-xl" : "rounded-br-xl",
        )}
        sizes="(max-width: 640px) 100vw, 320px"
      />

      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100",
          isStacked ? "rounded-xl" : "rounded-br-xl",
        )}
      />

      <FavoriteIconButton
        itemId={Number(rental.id)}
        svcType="rental"
        ariaLabel="Save service"
        active={rental.isFavorite ?? undefined}
        className="absolute left-2 top-2 z-10"
      />

      {isStacked && (
        <CardStatusBadges
          availMsg={rental.availMsg}
          className="absolute top-2 right-2 z-10"
        />
      )}

      {rental.overallReview?.overallRating && rental.totalReviews ? (
        <RatingReviewBadge
          rating={rental.overallReview.overallRating}
          reviewCount={rental.totalReviews}
          className="absolute left-2 bottom-2 z-10"
        />
      ) : null}

      {layout === "stacked" && (
        <CardStatusBadges
          disPercent={rental.disPercent}
          shadow={false}
          className="absolute right-2 bottom-2 z-10"
        />
      )}
    </div>
  );

  const contentBlock = (
    <section
      className={cn(
        "space-y-1",
        isStacked ? "py-1 mr-0.5" : "pt-2 pr-2 flex-1 min-w-0",
      )}
    >
      {layout === "stacked" && (
        <Typography
          variant="subtitle1"
          className="pt-1 line-clamp-2 group-hover:text-secondary px-2"
        >
          {rental.title}
        </Typography>
      )}

      {quickIcons.length > 0 ? (
        <div className={cardStyle}>
          {quickIcons.map((icon, index) => (
            <span key={`${icon}-${index}`}>
              <ServiceIcon icon={icon} className="h-4 w-4 text-body" />
            </span>
          ))}
        </div>
      ) : null}

      <div className={cardStyle}>
        {isBicycle ? (
          <IconBike
            className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4"
            strokeWidth={1.8}
          />
        ) : (
          <IconMotorbike
            className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4"
            strokeWidth={1.8}
          />
        )}
        <Typography variant="body2" className="truncate">
          {primaryMetaText}
        </Typography>
        {primaryMetaTrailing ? (
          <>
            <span
              aria-hidden="true"
              className="h-1 w-1 shrink-0 rounded-full bg-caption"
            />
            <Typography variant="body2" className="truncate text-body">
              {primaryMetaTrailing}
            </Typography>
          </>
        ) : null}
      </div>

      {layout === "row" && (
        <div className="flex gap-1">
          <IconMapPin
            className="h-4 w-4 shrink-0 text-body"
            strokeWidth={1.8}
          />
          <Typography variant="body2" className="line-clamp-2 text-body">
            {rental?.located ?? "-"}
          </Typography>
        </div>
      )}

      {layout !== "stacked" && (
        <div className="space-y-1">
          <CardStatusBadges
            isInstantConfirm={rental.isInstantConfirm}
            shadow={false}
            badgeClassNames={{
              instantConfirm: "!bg-transparent text-success px-0",
            }}
          />
          {rental.availMsg ? (
            <CardStatusBadges availMsg={rental.availMsg} shadow={false} />
          ) : null}
        </div>
      )}
    </section>
  );

  const footerBlock = (
    <div className="flex items-end justify-between gap-2 p-2">
      <div className="flex min-h-10 min-w-0 flex-1 flex-col justify-end">
        {layout !== "stacked" ? (
          <>
            {rental.mchntName ? (
              <div className="flex items-center gap-1 min-w-0 truncate">
                <IconBuildingStore
                  className="size-3.5 shrink-0 text-body"
                  strokeWidth={1.8}
                />
                <Typography
                  variant="body2"
                  className="truncate text-body"
                >
                  {rental.mchntName}
                </Typography>
              </div>
            ) : null}

            <Typography
              variant="subtitle1"
              className="line-clamp-1 group-hover:text-secondary mt-1"
            >
              {rental.title}
            </Typography>
          </>
        ) : (
          <div className="flex gap-1">
            <IconMapPin
              className="h-4 w-4 shrink-0 text-body"
              strokeWidth={1.8}
            />
            <Typography variant="body2" className="line-clamp-2 text-body">
              {rental?.located ?? "-"}
            </Typography>
          </div>
        )}
      </div>

      <div className="flex flex-col items-end shrink-0">
        <PriceDisplay
          price={price}
          salePrice={hasSalePrice ? salePrice : undefined}
          unit="/day"
          className="shrink-0"
        />
      </div>
    </div>
  );

  return (
    <Link href={finalHref} className="block h-full">
      <article
        className={cn(
          "relative flex bg-white h-full flex-col overflow-hidden rounded-2xl shadow-product-card group",
          isStacked ? "p-1" : "p-0",
          className,
        )}
        onClick={handleCardClick}
      >
        {layout !== "stacked" && discountLabel && (
          <RentalCornerDiscountBadge label={discountLabel} />
        )}

        {isStacked ? (
          <>
            {imageBlock}
            <div className="flex flex-1 flex-col">{contentBlock}</div>
          </>
        ) : (
          <div className="flex items-start gap-2">
            {imageBlock}
            {contentBlock}
          </div>
        )}

        {footerBlock}
      </article>
    </Link>
  );
};

export default memo(RentalCard);
